import React, { useContext, useRef } from 'react';
import styled from 'styled-components';
import { Drawer, Icon, List, ListItem, Tabs, TabPane, theme } from 'components';
import { AuthContext, TeamContext } from 'contexts';
import { useBreakpoint } from 'hooks';
import { featureEnabled } from 'utils';

import { isTeamAdmin } from '@bb/users';
import ListingActions from './ListingActions';
import ListingsCard from './ListingsCard';
import AppointmentsCard from './AppointmentsCard';
import ContactsCard from './ContactsCard';
import TasksCard from './TasksCard';
import TeamCard from './TeamCard';
import TeamListingsCard from './TeamListingsCard';
import TeamAppointmentsCard from './TeamAppointmentsCard';
import TeamTasksCard from './TeamTasksCard';
import TeamTeamCard from './TeamTeamCard';
import type { DashboardType } from '.';
import ActionsRequiredSection from './ActionsRequiredSection';

type MyBusinessPageContentProps = {
  dashboardType: DashboardType;
  isMobileDropdownOpen: boolean;
  setDashboardType?: React.Dispatch<React.SetStateAction<DashboardType>>;
  setIsMobileDropdownOpen?: React.Dispatch<React.SetStateAction<boolean>>;
};

const MyBusinessPageContent: React.FC<MyBusinessPageContentProps> = ({
  dashboardType,
  isMobileDropdownOpen,
  setDashboardType,
  setIsMobileDropdownOpen,
}) => {
  const { auth, hasFeature } = useContext(AuthContext);
  const { availableTeamStates, setTargetTeamId, targetTeamId } =
    useContext(TeamContext);
  const { md } = useBreakpoint();
  const drawerWrapper = useRef<HTMLDivElement>(null);

  const dataSource = [
    ...(auth.isLicensed
      ? [{ key: 'user', label: `${auth.firstName}'s Dashboard` }]
      : []),
    ...availableTeamStates.map(teamState => ({
      key: teamState.id,
      label: teamState.name,
    })),
  ];

  const mobileDrawerElement = (
    <Drawer
      placement="top"
      closable={false}
      open={isMobileDropdownOpen}
      getContainer={() => drawerWrapper.current!}
      height="auto"
      style={{ position: 'absolute' }}
      bodyStyle={{ padding: 0 }}
      drawerStyle={{
        background: 'white',
      }}
      zIndex={100}
    >
      <List
        dataSource={dataSource}
        renderItem={item => {
          const isChecked =
            (dashboardType === 'me' && item.key === 'user') ||
            (dashboardType === 'team' && item.key === targetTeamId);

          return (
            <ListItem
              onClick={() => {
                if (item.key === 'user') {
                  setDashboardType?.('me');
                } else {
                  setDashboardType?.('team');
                  setTargetTeamId(item.key);
                }
                setIsMobileDropdownOpen?.(false);
              }}
            >
              <DropdownItemContainer>
                <DropdownIconContainer>
                  {isChecked && <Icon type="check" color="primary" />}
                </DropdownIconContainer>{' '}
                {item.label}
              </DropdownItemContainer>
            </ListItem>
          );
        }}
      />
    </Drawer>
  );

  if (dashboardType === 'me') {
    const notTaAndHaveTeams = !isTeamAdmin(auth) && auth.teams.length > 0;

    // A team admin user must not see the Team card in their agent dashboard
    const shouldShowTeamCard = hasFeature('TEAMS') && notTaAndHaveTeams;

    if (!md) {
      return (
        <StyledDrawerWrapper ref={drawerWrapper}>
          {mobileDrawerElement}
          {featureEnabled('showingSettingsFeature') && (
            <ActionsRequiredSection />
          )}
          <Tabs
            type="primary"
            defaultActiveKey="listings"
            tabBarExtraContent={{
              right: <ListingActions isDropdown />,
            }}
          >
            <TabPane tab="Listings" key="listings">
              <ListingsCard />
            </TabPane>
            <TabPane tab="Showings" key="appointments">
              <AppointmentsCard />
            </TabPane>
            <TabPane tab="Tasks" key="tasks">
              <TasksCard />
            </TabPane>
            {shouldShowTeamCard && (
              <TabPane tab="Team" key="team">
                <TeamCard />
              </TabPane>
            )}
            <TabPane tab="Contacts" key="contacts">
              <ContactsCard />
            </TabPane>
          </Tabs>
        </StyledDrawerWrapper>
      );
    }

    return (
      <>
        {featureEnabled('showingSettingsFeature') && <ActionsRequiredSection />}
        <CardsContainer>
          <ListingsCard />
          <AppointmentsCard />
          <TasksCard />
          {shouldShowTeamCard && <TeamCard />}
          <ContactsCard />
        </CardsContainer>
      </>
    );
  }

  if (!md) {
    return (
      <StyledDrawerWrapper ref={drawerWrapper}>
        {mobileDrawerElement}
        <Tabs defaultActiveKey="listings" type="primary">
          <TabPane tab="Team Listings" key="listings">
            <TeamListingsCard />
          </TabPane>
          <TabPane tab="Team Showings" key="appointments">
            <TeamAppointmentsCard />
          </TabPane>
          <TabPane tab="Team Tasks" key="tasks">
            <TeamTasksCard />
          </TabPane>
          <TabPane tab="Team" key="team">
            <TeamTeamCard />
          </TabPane>
        </Tabs>
      </StyledDrawerWrapper>
    );
  }

  return (
    <CardsContainer>
      <TeamListingsCard />
      <TeamAppointmentsCard />
      <TeamTasksCard />
      <TeamTeamCard />
    </CardsContainer>
  );
};

export default MyBusinessPageContent;

const StyledDrawerWrapper = styled.div`
  position: relative;
`;

const CardsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const DropdownItemContainer = styled.div`
  display: flex;
  align-items: center;

  height: 40px;
  font-size: 16px;
  font-weight: 500;
  color: ${theme.gray8};
`;

const DropdownIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 52px;
`;
